import isNil from 'lodash/isNil'
import React from 'react'

import { roundNumber } from '@ztechapps/common.utils'
import { useTrackEvent } from '@ztechapps/webui.telemetry'

import { useContext } from '../Context'

const filterMaxSize = {
  verticalMaxin: 1000,
  verticalMaxmm: 1000,
  horizontalMaxin: 1000,
  horizontalMaxmm: 1000,
}

export type SizeValue = {
  start?: number
  end?: number
  mid?: number
}

export enum MeasureType {
  in = 'in',
  mm = 'mm',
}

export const useSizeFilter = ({ type }: { type: 'vertical' | 'horizontal' }) => {
  const trackEvent = useTrackEvent()
  const { onChangeFilter, ...rest } = useContext()
  const variance = 0.1

  const [value, setValue] = React.useState<SizeValue>()
  const measureType = rest?.[`${type}MeasureType`] ?? MeasureType.in

  const maxSize = filterMaxSize?.[`${type}Max${measureType}`]
  const minFilter = rest?.[`${type}MinFilter`]
  const maxFilter = rest?.[`${type}MaxFilter`]
  const midFilter = rest?.[`${type}MidFilter`]
  const maxInches = filterMaxSize?.[`${type}Maxin`]
  const maxMm = filterMaxSize?.[`${type}Maxmm`]
  const [maximumValue, setMaximumValue] = React.useState(maxInches)

  React.useEffect(() => {
    if (maxFilter === null && minFilter === null && midFilter === null) return

    //if the value of the saved measureType is mm,  set the maxValue and step to default mm values
    if (measureType === MeasureType.mm) {
      setStep(1)
    }
    //if the value of the saved measureType is in,  set the maxValue and step to default in values
    else if (measureType === MeasureType.in) {
      setStep(0.01)
    }
    setMaximumValue(maxSize)

    //if the value of the saved measureType is all,  set the maxValue and step to default in values
    if (!measureType) {
      setMaximumValue(maxFilter)
    }

    const start = minFilter
    const end = maxFilter
    setValue({ start, end, mid: midFilter })
  }, [minFilter, maxFilter, midFilter, measureType, maxMm, maxSize])

  const roundSize = measureType === MeasureType.in ? 3 : 1

  const [step, setStep] = React.useState(0.01)

  const onChange = (e: SizeValue) => {
    trackEvent('Size filter', {
      type,
      start: String(e?.start ?? ''),
      end: String(e?.end ?? ''),
      measure_type: measureType,
    })

    let endValue = e?.end
    let startValue = e?.start
    const midValue = e?.mid

    //but if the end value and start value are null, only the mid is provided, we need to compute the actual start -5% of actual and end as +5% if actual
    if (isNil(startValue) && isNil(endValue)) {
      endValue = roundNumber(midValue * (1 + variance), roundSize)
      startValue = roundNumber(midValue * (1 - variance), roundSize)
      //if less than zero, stick to zero
      if (startValue < 0) startValue = 0
      if (endValue > maxSize) endValue = maxSize
    }

    //update the value
    setValue({ start: startValue, end: endValue, mid: midValue })

    //always reset the filter if the start is zero and the end is the maximum measure_type value
    onChangeFilter({
      [`${type}MaxFilter`]: endValue,
      [`${type}MinFilter`]: startValue,
      [`${type}MidFilter`]: midValue,
      [`${type}MeasureType`]: measureType,
    })
  }

  const onChangeMeasureType = (e) => {
    const _measureType = e?.target?.value

    if (_measureType === MeasureType.in) {
      setMaximumValue(maxInches)
      setStep(0.01)
      setValue({ start: 0, end: maxInches, mid: midFilter })
    } else if (_measureType === MeasureType.mm) {
      setMaximumValue(maxMm)
      setStep(1)
      setValue({ start: 0, end: maxMm, mid: midFilter })
    }

    onChangeFilter({ [`${type}MidFilter`]: midFilter, [`${type}MeasureType`]: _measureType })

    trackEvent('Size filter', {
      type,
      measure_type: _measureType,
    })
  }

  return { value, measureType, maximumValue, onChange, onChangeMeasureType, step }
}
